import PaymentMethodTab from "components/ui-component/PaymentMethodTab";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CardFormComponent from "./CardFormComponent";
import InputField from "components/form/InputField";
import BankMethodComponent from "./BankMethodComponent";
import { updateOverlayState, useUserData } from "store/slices/authSlice";
import InfoIcon from "icons/dashboard/InfoIcon";
import WalletIcon from "icons/payments/WalletIcon";
import LegacyGooglePayButton from "@google-pay/button-react";
import { Elements } from "@stripe/react-stripe-js";
import { GOOGLE_MERCHANT_ID, GOOGLE_MERCHANT_NAME } from "store/api/constApi";
import {
  getIntent,
  getPlaidLinkToken,
  markIncompletePayment,
  setIsPaymentProcessing,
} from "store/slices/paymentsSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { loadStripe } from "@stripe/stripe-js";
import CashAppForm from "./CashAppForm";
import {
  leadData,
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import { useLocation, useParams } from "react-router-dom";
import { useIsLogin } from "store/slices/commonSlice";
import { CashAppMobileIcon } from "icons/payments/CashAppMobileIcon";
import moment from "moment";
import Button from "components/form/Button";
import StripeBankForm from "./StripeBankForm";
import {
  checkBankManualFormOrPlaid,
  checkPhone,
  detectCountry,
  formatCurrency,
  getCountryShortName,
  validateEmail,
} from "utils/HelperFunctions";
import ApplePayButton from "components/payments/ApplePayButton";
import AmazonPayForm from "./AmazonPayForm";
import { decode } from "utils/HelperFunctions";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { useBankData, useCardData } from "store/slices/paymentMethodSlice";
import { PaymentGateway, SettingsValue } from "types/payments";
import PlaidBankForm from "./PlaidBankForm";
import StripeCardPaymentCheckoutForm from "./StripeCardPaymentCheckoutForm";
import { CardFormCheckType, PhoneInput } from "types/common";
import { getCountryCallingCode } from "react-phone-number-input";
import { CountryCode } from "types/UserProfile";
import { PaymentComponentProps } from "types/paymentMethodsComponent";
import { RootState } from "store/store";
import MobileInputNew from "components/form/MobileInputNew";
import PayPalForm from "./PayPalForm";
import StaxCardForm from "./StaxCardForm";
import StaxBankForm from "./StaxBankForm";
import { VerifyStripeCardPaymentParams } from "types/agreement";
import { ConvenienceFeeDisplay } from "./PaymentMethodComponents";
import { useTestAttrsFactory } from "utils/testid";
import {
  getPaymentTotalWithFee,
  hasValidPaymentPrincipal,
} from "utils/paymentFee";
import GooglePayButton, {
  DisabledGooglePayButton,
} from "../payments/GooglePayButton";
import {
  isStripeKeyMissing,
  resolveStripePublishableKey,
  STRIPE_CARD_UNAVAILABLE_MESSAGE,
} from "utils/stripePublishableKey";
import {
  getIntentOrderId,
  getPaymentFailureReason,
} from "utils/incompleteOrder";

const GOOGLE_PAY_LOG_PREFIX = "[Stripe Google Pay][parent]";

const noopSetPhoneNumber: React.Dispatch<
  React.SetStateAction<PhoneInput>
> = () => {};

const PaymentMethodsComponent = ({
  cardData,
  bankData,
  paymentConfig,
  paymentHandlers,
  paymentUrls,
  formState,
  paymentGatewayState,
  componentRefs,
  phoneAndAddressConfig,
  accounts_id = "",
  description = "Online Payment",
  noAuthClusterId = "",
}: PaymentComponentProps) => {
  const testAttrs = useTestAttrsFactory();
  const {
    cardNumber,
    isDateErr,
    isErr,
    cardErrMsg,
    handleChangeCard,
    validateCard,
    iconType,
    setIconType = (val: string) => {},
    setIsAddNewCardMakePayment,
    isAddNewCardMakePayment,
  } = cardData;

  const {
    bankName,
    numberNotmatchedError,
    setnumberNotmatchedError,
    handleChangeBank,
    selectedState,
    setSelectedState,
    stripeBankURL = "",
    isManualForm = true,
    setIsManualForm = () => {},
    handlePlaidBankPayment = async (
      val: any,
      isCCTokenAvailable: boolean
    ): Promise<void> => {
      return Promise.resolve();
    },
    checkNegativeBalance = false,
  } = bankData;

  const {
    paymentMethodList,
    selectedMethod,
    setSelectedMethod,
    amount = 1000,
    isRecurring,
    isPaymentLink,
    isEdit = false,
    setIsEdit = () => {},
    isRetry = false,
    isAgreement = false,
    isUpdatePlan = false,
    isUpdateSubscription = false,
    isUpdateSubscriptionPaymentMethod = false,
    isDowngradePlan,
    isSchedulePayment = false,
    isProductLink = false,
    loadBankFormBeforeEmail = false,
    customClassName = "",
    dataArr = {},
    custom_conv = "on",
    isStaxPayPalLoading,
    handleEmailBlur = () => {},
    isInvoice = false,
    useStripeGooglePay = false,
    useStripeApplePay = false,
    agreementClusterId,
    agreementLeadId,
    agreementId,
    retryOrderSlug = "",
  } = paymentConfig;

  const {
    handleGooglePay = null,
    handleApplePay = () => {},
    createPayPalIntent = () => {},
    handleBack = () => {},
    setShowPreview = () => {},
    verifyStaxCardBank = async ({
      cc_token = "",
      cc_token2 = "",
      x_method = "",
    }) => {
      return Promise.resolve();
    },
    verifyStaxPayPal = async ({
      transactionId = "",
      error,
    }: {
      transactionId?: string;
      error?: any;
    }) => {
      return Promise.resolve();
    },
    verifyStripeCardPayment = async ({
      intent,
      error,
      email,
      orderId,
    }: VerifyStripeCardPaymentParams): Promise<void> => {
      return Promise.resolve();
    },
  } = paymentHandlers;

  const { cashAppURL = "", stripeAmazonPayURL = "" } = paymentUrls;

  const {
    values,
    email,
    setEmail,
    errMsg,
    amtErr = null,
    setAmtErr = () => {},
    isRingLoading,
  } = formState;

  const {
    isGoogleLoading = false,
    hasGooglePay = true,
    disableInputFields = false,
  } = paymentGatewayState;

  const { cashAppRef, bankRef, cardRef, amazonRef } = componentRefs;

  const {
    collectAddress = true,
    collectPhone = false,
    phoneNumber = { phone: "", postal_code: "", country_code: "" },
    setPhoneNumber = noopSetPhoneNumber,
  } = phoneAndAddressConfig;
  const { paymentPermission, conFee, isFeeLoading, companyDetails } =
    useAppSelector((state: RootState) => state.payment);
  const cardKey = resolveStripePublishableKey(paymentPermission, "CC");
  const bankKey = resolveStripePublishableKey(paymentPermission, "ACH");
  const amazonKey = resolveStripePublishableKey(paymentPermission, "AMZP");
  const cashAppKey = resolveStripePublishableKey(paymentPermission, "CAPP");
  const googlePayKey = resolveStripePublishableKey(paymentPermission, "GP");

  // Dynamic Stripe promises for Card, Bank, Amazon Pay, Cash App Pay, Google Pay
  const [stripePromiseCard, setStripePromiseCard] = useState<any>(null);
  const [stripePromiseBank, setStripePromiseBank] = useState<any>(null);
  const [stripePromiseAmazon, setStripePromiseAmazon] = useState<any>(null);
  const [stripePromiseCashApp, setStripePromiseCashApp] = useState<any>(null);
  const [stripePromiseGooglePay, setStripePromiseGooglePay] =
    useState<any>(null);

  useEffect(() => {
    console.info(`${GOOGLE_PAY_LOG_PREFIX} Stripe configuration changed`, {
      hasPublishableKey: Boolean(googlePayKey),
      useStripeGooglePay,
    });
    setStripePromiseCard(cardKey ? loadStripe(cardKey) : null);
    setStripePromiseBank(bankKey ? loadStripe(bankKey) : null);
    setStripePromiseAmazon(amazonKey ? loadStripe(amazonKey) : null);
    setStripePromiseCashApp(cashAppKey ? loadStripe(cashAppKey) : null);
    setStripePromiseGooglePay(googlePayKey ? loadStripe(googlePayKey) : null);
  }, [
    cardKey,
    bankKey,
    amazonKey,
    cashAppKey,
    googlePayKey,
    useStripeGooglePay,
  ]);
  const userData = useUserData();
  const { clusterId: cluster_id, id: actionId } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const resolvedClusterId = clusterId
    ? clusterId
    : isAgreement && agreementClusterId
      ? agreementClusterId
      : actionId
        ? decode(actionId || "")?.split("_")[1]
        : userData?.data?.accountData?.clusterid;
  // `isPaymentLink` is also used by public make-payment screens for UI layout.
  // Only a `pl_` route is a payment-link request at the API boundary.
  const isActualPaymentLink = Boolean(
    isPaymentLink && actionId?.startsWith("pl_")
  );
  const [clientSecret, setClientSecret] = useState<string | undefined>(
    undefined
  );
  const [bankSecret, setBankSecret] = useState<string | undefined>(undefined);
  const [amazonSecret, setAmazonSecret] = useState<string | undefined>(
    undefined
  );
  const [cashAppOrderId, setCashAppOrderId] = useState<number>(0);
  const [bankOrderId, setBankOrderId] = useState<number>(0);
  const [amazonOrderId, setAmazonOrderId] = useState<number>(0);
  const [googlePayOrderId, setGooglePayOrderId] = useState<number>(0);
  const [googlePaySecret, setGooglePaySecret] = useState<string | undefined>(
    undefined
  );
  const [isGoogleIntentLoading, setIsGoogleIntentLoading] =
    useState<boolean>(false);
  const googleIntentRequestId = useRef(0);
  const googleIntentFingerprint = useRef<string | null>(null);
  const [isApplePay, setIsApplePay] = useState<boolean>(true);
  const [isGooglePay, setIsGooglePay] = useState<boolean>(true);

  let accountData = userData?.data?.accountData;
  const oldCardData = useCardData();
  const oldBankData = useBankData();

  const accountLeadData = useLeadData();
  const portalSettings = usePortalSettingsData();
  const isLogin = useIsLogin();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);
  const [isLinkTokenLoading, setIsLinkTokenLoading] = useState<boolean>(false);
  const [isEmailInputBlur, setIsEmailInputBlur] = useState<boolean>(true);
  const [formCheck, setFormCheck] = useState<CardFormCheckType>({
    card: false,
    expiry: false,
    cvv: false,
  });
  const [formEmptyCheck, setFormEmptyCheck] = useState<CardFormCheckType>({
    card: true,
    expiry: true,
    cvv: true,
  });

  const handlePhoneCountryChange = useCallback((itm: string) => {
    setPhoneNumber((state) => ({
      ...state,
      country_code: getCountryCallingCode(itm as CountryCode),
    }));
  }, []);

  const handleOnPhoneChange = useCallback((phone: string) => {
    setPhoneNumber((state) => ({
      ...state,
      phone,
    }));
  }, []);

  const onGetLeadData = async () => {
    let lead_payload: any = {
      accounts_id: userData.data.accountData.id,
      cluster_id: userData.data.accountData.clusterid,
      accountid: userData.data.accountData.accountid,
    };
    try {
      await dispatch(leadData(lead_payload)).unwrap();
    } catch (error: any) {
      console.log("onGetLeadData error", error);
    }
  };

  useEffect(() => {
    if (
      isLogin &&
      accountLeadData &&
      Object.keys(accountLeadData).length === 0
    ) {
      onGetLeadData();
    }
  }, [isLogin]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    if (accountData?.email) {
      setEmail(accountData?.email || "");
    }
  }, [accountData]);

  const dispatch = useAppDispatch();

  const markIntentFailed = useCallback(
    async (orderId: number, error: any) => {
      if (!orderId) return;
      try {
        await dispatch(
          markIncompletePayment({
            orderId,
            cluster_id: resolvedClusterId || undefined,
            failed_reason: getPaymentFailureReason(error),
          })
        ).unwrap();
      } catch (markError) {
        console.error("Unable to mark incomplete payment failed", markError);
      }
    },
    [dispatch, resolvedClusterId]
  );

  const getRecurringResponse = () => {
    if (
      isAgreement ||
      isUpdatePlan ||
      isUpdateSubscriptionPaymentMethod ||
      isDowngradePlan
    ) {
      return isRecurring;
    } else {
      if (isRecurring) {
        return !moment(
          dataArr?.transactions?.[0]?.date ??
            dataArr?.transactions?.[0]?.payment_date
        ).isSame(moment(), "days");
      } else {
        return false;
      }
    }
  };
  const buildIntentPayload = (
    payment_method_types: string,
    gateway_method?: "GP"
  ) => {
    const clusterid = resolvedClusterId;
    let payment_link_id = null;
    if (isActualPaymentLink) {
      const decoded = decode(actionId || "");
      if (decoded) {
        payment_link_id = decoded?.split("_")[0];
      }
    }
    const normalizedAmount = Number(String(amount).replace(/,/g, ""));

    return {
      name: (accountData?.first_name || "") + (accountData?.last_name || ""),
      email: email ?? accountData?.email,
      phone_number: accountData?.phone,
      total_amount: Math.round(normalizedAmount * 100),
      description: "Online Payment",
      payment_note: description,
      currency: "usd",
      payment_method_types,
      ...(gateway_method ? { gateway_method } : {}),
      // Google Pay is first saved with a SetupIntent. The existing wallet
      // verification endpoint then charges it or creates the schedule.
      isRecurring: gateway_method === "GP" ? true : getRecurringResponse(),
      stripe_fin: payment_method_types === "us_bank_account" ? "Y" : "N",
      accountid: userData?.data?.accountData?.accountid,
      cluster_id: clusterid,
      leadid: isAgreement ? agreementLeadId : accountLeadData?.lead_id,
      isAgreement,
      isPaymentLink: isActualPaymentLink,
      isRetry,
      ...(isRetry && retryOrderSlug
        ? { order_id: Number(decode(retryOrderSlug || "")?.split("_")[0]) || 0 }
        : {}),
      ...(isAgreement && agreementId ? { agreement_id: agreementId } : {}),
      custom_conv,
      payment_link_id,
      // The agreement slug lets the server refuse the intent before the card
      // is charged when the agreement could not be signed afterwards.
      ...((isActualPaymentLink || isInvoice || isAgreement) && actionId
        ? { slug: actionId }
        : {}),
    };
  };

  const getSecret = async (
    payment_method_types: string,
    gateway_method?: "GP",
    requestId?: number,
    preparedPayload?: ReturnType<typeof buildIntentPayload>,
    requestFingerprint?: string
  ) => {
    const isGooglePayIntent = gateway_method === "GP";
    let shouldRetainGooglePayFingerprint = false;
    if (isGooglePayIntent) {
      setIsGoogleIntentLoading(true);
    } else {
      setIsLoading(true);
    }
    try {
      const payload =
        preparedPayload ||
        buildIntentPayload(payment_method_types, gateway_method);
      if (isGooglePayIntent) {
        console.info(`${GOOGLE_PAY_LOG_PREFIX} requesting SetupIntent`, {
          amountIsValid: Number.isFinite(payload.total_amount),
          requestId,
        });
      }
      if (isGooglePayIntent) {
        console.info(`${GOOGLE_PAY_LOG_PREFIX} SetupIntent request prepared`, {
          gatewayMethod: payload.gateway_method,
          stripePaymentMethodType: payload.payment_method_types,
        });
      }
      let res: any = await dispatch(getIntent(payload));
      if (
        isGooglePayIntent &&
        requestId !== undefined &&
        requestId !== googleIntentRequestId.current
      ) {
        console.info(`${GOOGLE_PAY_LOG_PREFIX} ignored stale response`, {
          activeRequestId: googleIntentRequestId.current,
          requestId,
        });
        return;
      }
      if (res?.payload?.body?.success) {
        const orderId = getIntentOrderId(res);
        if (payment_method_types === "cashapp") {
          setCashAppOrderId(orderId);
          setClientSecret(res?.payload?.body?.data?.client_secret);
        } else if (payment_method_types === "amazon_pay") {
          setAmazonOrderId(orderId);
          dispatch(setIsPaymentProcessing(true));
          setAmazonSecret(res?.payload?.body?.data?.client_secret);
        } else if (isGooglePayIntent) {
          setGooglePayOrderId(orderId);
          const responseGatewayMethod =
            res?.payload?.body?.data?.gateway_method;
          const responseClientSecret = res?.payload?.body?.data?.client_secret;
          console.info(`${GOOGLE_PAY_LOG_PREFIX} SetupIntent received`, {
            gatewayMethod: responseGatewayMethod || "missing",
            hasClientSecret: Boolean(responseClientSecret),
            requestId,
          });
          if (responseGatewayMethod && responseGatewayMethod !== "GP") {
            console.error(
              `${GOOGLE_PAY_LOG_PREFIX} SetupIntent method mismatch`,
              {
                expectedGatewayMethod: "GP",
                responseGatewayMethod,
                requestId,
              }
            );
            setGooglePaySecret(undefined);
            setIsGooglePay(false);
            return;
          }
          if (!responseClientSecret) {
            console.error(
              `${GOOGLE_PAY_LOG_PREFIX} SetupIntent client secret missing`,
              { requestId }
            );
            setGooglePaySecret(undefined);
            return;
          }
          shouldRetainGooglePayFingerprint = true;
          setGooglePaySecret(responseClientSecret);
        } else {
          setBankOrderId(orderId);
          dispatch(setIsPaymentProcessing(true));
          setBankSecret(res?.payload?.body?.data?.client_secret);
        }
      } else if (isGooglePayIntent) {
        console.error(`${GOOGLE_PAY_LOG_PREFIX} SetupIntent request failed`, {
          message: res?.payload?.body?.data?.msg || "Unknown backend error",
          requestId,
        });
        setGooglePaySecret(undefined);
      }
    } catch (err) {
      if (isGooglePayIntent) {
        console.error(`${GOOGLE_PAY_LOG_PREFIX} SetupIntent exception`, {
          message: err instanceof Error ? err.message : String(err),
          requestId,
        });
        setGooglePaySecret(undefined);
      } else {
        console.log("err =====", err);
      }
    } finally {
      if (
        isGooglePayIntent &&
        requestFingerprint &&
        !shouldRetainGooglePayFingerprint &&
        requestFingerprint === googleIntentFingerprint.current
      ) {
        googleIntentFingerprint.current = null;
      }
      if (
        !isGooglePayIntent ||
        requestId === undefined ||
        requestId === googleIntentRequestId.current
      ) {
        isGooglePayIntent
          ? setIsGoogleIntentLoading(false)
          : setIsLoading(false);
      }
    }
  };

  const getPlaidToken = async () => {
    setIsLinkTokenLoading(true);
    try {
      let payload = {
        client_name:
          (companyDetails?.name || companyDetails?.company_name) ??
          "Palm Labs LLC",
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name?.trim(),
        email: email || userData?.data?.accountData?.email,
        isProductLink: isProductLink,
        clusterid: userData?.data?.accountData?.clusterid ?? noAuthClusterId,
        slug: actionId || cluster_id || "",
      };
      let res: any = await dispatch(getPlaidLinkToken(payload)).unwrap();
      if (res?.body?.success) {
        setPlaidLinkToken(res?.body?.data?.link_token || null);
      }
      setIsLinkTokenLoading(false);
    } catch (error) {
      console.log("getPlaidToken error =====", error);
      setIsLinkTokenLoading(false);
    }
  };

  const { isChecking, isSubscriptionAllowed } = useAppSelector(
    (state: RootState) => state.subscription
  );

  useEffect(() => {
    let checkAmount = !getRecurringResponse() ? amount >= 1 : true;

    // Stripe bank (quick checkout): create the intent as soon as Bank is
    // selected, like make payment, so the bank form (which has its own email
    // field) loads; the product-link email gate below would block it. Once,
    // per amount: the form remounts when the amount changes.
    const isStripeBankSelected =
      paymentMethodList?.some((item: any) => item?.name === "Bank") &&
      selectedMethod === "Bank" &&
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE;
    if (
      loadBankFormBeforeEmail &&
      isStripeBankSelected &&
      !bankSecret &&
      !isLoading &&
      !isEdit &&
      !isRetry &&
      checkAmount &&
      !amtErr
    ) {
      setIsLoading(true);
      getSecret("us_bank_account");
    }

    if (
      isProductLink &&
      (!validateEmail(email) || !isSubscriptionAllowed || isChecking)
    ) {
      return;
    }

    if (collectPhone && !checkPhone(phoneNumber)) {
      return;
    }
    // cash app
    if (
      paymentMethodList?.length > 0 &&
      paymentMethodList?.some((item: any) => item?.name === "Cash App Pay") &&
      selectedMethod === "Cash App Pay" &&
      !isEdit &&
      !searchParams.has("payment_intent") &&
      !searchParams.has("setup_intent") &&
      checkAmount &&
      !isRetry &&
      !amtErr
    ) {
      setIsLoading(true);
      getSecret("cashapp");
    }
    // bank
    if (
      paymentMethodList?.length > 0 &&
      paymentMethodList?.some((item: any) => item?.name === "Bank") &&
      selectedMethod === "Bank" &&
      !isEdit &&
      !isRetry &&
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE &&
      !isLoading &&
      checkAmount &&
      !isRetry &&
      !amtErr &&
      !loadBankFormBeforeEmail
    ) {
      setIsLoading(true);
      getSecret("us_bank_account");
    }
    let check = checkBankManualFormOrPlaid({
      manual_ach: portalSettings?.manual_ach,
      validate_bank_account: portalSettings?.validate_bank_account,
      isManualForm,
    });

    if (
      paymentMethodList?.length > 0 &&
      paymentMethodList?.some((item: any) => item?.name === "Bank") &&
      selectedMethod === "Bank" &&
      !isEdit &&
      isEmailInputBlur &&
      email &&
      validateEmail(email) &&
      paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE &&
      !isLoading &&
      check &&
      checkAmount &&
      !isRetry &&
      !amtErr &&
      !plaidLinkToken
    ) {
      getPlaidToken();
    }
    // amazon pay
    if (
      paymentMethodList?.length > 0 &&
      paymentMethodList?.some((item: any) => item?.name === "Amazon Pay") &&
      selectedMethod === "Amazon Pay" &&
      !isEdit &&
      !isRetry &&
      !isLoading &&
      checkAmount &&
      !isRetry &&
      !amtErr
    ) {
      setIsLoading(true);
      getSecret("amazon_pay");
    }

    const shouldPrepareGooglePay =
      useStripeGooglePay &&
      paymentMethodList?.some((item: any) => item?.name === "Card") &&
      paymentPermission?.gp_ext_availability === SettingsValue.YES &&
      Boolean(googlePayKey) &&
      (paymentPermission?.ap_ext_availability !== SettingsValue.YES ||
        !isApplePay) &&
      !isEdit &&
      checkAmount &&
      hasValidPaymentPrincipal(amount) &&
      !amtErr &&
      !(isProductLink && !validateEmail(email)) &&
      !(collectPhone && !checkPhone(phoneNumber));

    console.info(`${GOOGLE_PAY_LOG_PREFIX} eligibility evaluated`, {
      amountAllowed: checkAmount,
      amountHasError: amtErr,
      applePayDoesNotTakePriority:
        paymentPermission?.ap_ext_availability !== SettingsValue.YES ||
        !isApplePay,
      cardMethodAvailable: Boolean(
        paymentMethodList?.some((item: any) => item?.name === "Card")
      ),
      googlePayEnabled:
        paymentPermission?.gp_ext_availability === SettingsValue.YES,
      hasPublishableKey: Boolean(googlePayKey),
      isEdit,
      isRetry,
      phoneValid: !(collectPhone && !checkPhone(phoneNumber)),
      productLinkEmailValid: !(isProductLink && !validateEmail(email)),
      shouldPrepareGooglePay,
      useStripeGooglePay,
    });

    let googlePayTimer: ReturnType<typeof setTimeout> | undefined;
    let googlePayRequestStarted = false;
    let scheduledGooglePayFingerprint: string | null = null;
    if (shouldPrepareGooglePay) {
      const googlePayPayload = buildIntentPayload("card", "GP");
      scheduledGooglePayFingerprint = JSON.stringify(googlePayPayload);

      if (googleIntentFingerprint.current === scheduledGooglePayFingerprint) {
        console.info(
          `${GOOGLE_PAY_LOG_PREFIX} duplicate SetupIntent request skipped`
        );
      } else {
        const requestId = ++googleIntentRequestId.current;
        googleIntentFingerprint.current = scheduledGooglePayFingerprint;
        console.info(`${GOOGLE_PAY_LOG_PREFIX} scheduling SetupIntent`, {
          requestId,
        });
        setGooglePaySecret(undefined);
        googlePayTimer = setTimeout(() => {
          googlePayRequestStarted = true;
          getSecret(
            "card",
            "GP",
            requestId,
            googlePayPayload,
            scheduledGooglePayFingerprint || undefined
          );
        }, 350);
      }
    } else {
      googleIntentRequestId.current += 1;
      googleIntentFingerprint.current = null;
      console.info(`${GOOGLE_PAY_LOG_PREFIX} clearing Google Pay state`, {
        requestId: googleIntentRequestId.current,
      });
      setGooglePaySecret(undefined);
      setIsGoogleIntentLoading(false);
    }

    return () => {
      if (googlePayTimer) {
        clearTimeout(googlePayTimer);
        if (
          !googlePayRequestStarted &&
          googleIntentFingerprint.current === scheduledGooglePayFingerprint
        ) {
          googleIntentFingerprint.current = null;
        }
        console.info(`${GOOGLE_PAY_LOG_PREFIX} cancelled SetupIntent timer`);
      }
    };
  }, [
    isEdit,
    email,
    selectedMethod,
    amtErr,
    portalSettings?.manual_ach,
    portalSettings?.validate_bank_account,
    isManualForm,
    isEmailInputBlur,
    isRetry,
    amount,
    phoneNumber?.phone,
    phoneNumber?.country_code,
    isSubscriptionAllowed,
    isChecking,
    isApplePay,
    paymentPermission?.ap_ext_availability,
    paymentPermission?.gp_ext_availability,
    googlePayKey,
    useStripeGooglePay,
  ]);

  useEffect(() => {
    console.info(`${GOOGLE_PAY_LOG_PREFIX} render state changed`, {
      googlePayAvailable: isGooglePay,
      hasClientSecret: Boolean(googlePaySecret),
      hasStripePromise: Boolean(stripePromiseGooglePay),
      intentLoading: isGoogleIntentLoading,
    });
  }, [
    googlePaySecret,
    isGoogleIntentLoading,
    isGooglePay,
    stripePromiseGooglePay,
  ]);

  const bankFormOptions = {
    clientSecret: bankSecret,
    appearance: {
      variables: {
        fontFamily: "DM Sans, sans-serif",
        colorDanger: "#ff0000",
        fontSizeSm: "12px",
        fontSizeXs: "12px",
        colorText: "#19224c",
        colorTextSecondary: "#19224c",
        colorPrimary: "#1678f2",
        colorBackgroundDeemphasize10: "#dfe4ec",
        borderRadius: "6px",
      },
      rules: {
        ".Label": {
          color: "#8c90a5",
          fontWeight: "700",
          fontSize: "12px",
          textTransform: "uppercase",
          display: "none",
        },
        ".Input::placeholder": {
          fontWeight: "400",
          fontSize: "12px",
          color: "#8c90a5",
        },
        ".Input": {
          border: "1px solid #dfe4ec",
          fontWeight: "400",
          fontSize: "12px",
          color: "#8c90a5",
        },
        ".Error": {
          fontSize: "12px",
          fontWeight: "400",
        },
        ".PickerItem": {
          border: "1px solid #dfe4ec",
          borderRadius: "6px",
        },
      },
    },
  };

  const getColumns = (value: number = 0) => {
    switch (value) {
      case 1:
        return `xl:grid-cols-1 md2:grid-cols-1`;
      case 2:
        return `xl:grid-cols-2 md2:grid-cols-2`;
      case 3:
        return `xl:grid-cols-3 md2:grid-cols-3`;
      case 4:
        return `xl:grid-cols-2 md2:grid-cols-2`;
      case 5:
        return `xl:grid-cols-3 md2:grid-cols-3`;
      case 6:
        return `xl:grid-cols-3 md2:grid-cols-3`;
      default:
        return `xl:grid-cols-3 md2:grid-cols-3`;
    }
  };

  useEffect(() => {
    const setCountry = async () => {
      const userCountry = await detectCountry();
      userCountry && handleChangeCard({ value: userCountry }, "x_country");
    };
    setCountry();
  }, []);

  const showConvFee = useMemo(() => {
    if (!hasValidPaymentPrincipal(amount)) return false;

    if (
      paymentPermission?.cc_gateway_name?.name === PaymentGateway.STAX &&
      selectedMethod === "Card"
    ) {
      return true;
    } else if (
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX &&
      selectedMethod === "Bank"
    ) {
      return true;
    }

    if (portalSettings?.convenience_fee_opt === SettingsValue.YES) {
      return true;
    } else if (
      portalSettings?.convenience_fee_opt === SettingsValue.CUSTOM &&
      custom_conv === "on"
    ) {
      return true;
    } else if (
      portalSettings?.convenience_fee_opt === SettingsValue.CUSTOM &&
      (custom_conv === "off" || custom_conv === "")
    ) {
      return false;
    } else if (portalSettings?.convenience_fee_opt === SettingsValue.NO) {
      return false;
    }
    return conFee > 0;
  }, [amount, portalSettings, custom_conv, conFee]);

  const amountCheck = useMemo(() => {
    if (getRecurringResponse()) {
      return false;
    }

    return isEdit || amount < 1.0 || amount > 20000;
  }, [isEdit, amount, getRecurringResponse()]);

  const isGooglePayAmountDisabled =
    !hasValidPaymentPrincipal(amount) || amountCheck || Boolean(amtErr);

  const stripeKey = googlePayKey;
  return (
    <div
      className={`flex flex-col lg:gap-10 gap-5 text-LightTextParagraph mt-2 ${customClassName}`}
      {...testAttrs("payment-method-form", "Payment method form")}
    >
      {/* GOOGLEPAY APPLEPAY */}
      {paymentMethodList?.some((item: any) => item?.name === "Card") &&
        hasGooglePay &&
        (isApplePay &&
        paymentPermission?.ap_ext_availability === SettingsValue.YES ? (
          <div {...testAttrs("express-wallets", "Express wallet payments")}>
            <ApplePayButton
              isEdit={isEdit}
              amount={amount}
              totalAmount={getPaymentTotalWithFee(amount, conFee)}
              email={email}
              disabled={
                isFeeLoading ||
                disableInputFields ||
                (isProductLink && !validateEmail(email)) ||
                (checkNegativeBalance && amountCheck)
              }
              isApplePay={isApplePay}
              isRecurring={
                isAgreement || isSchedulePayment ? true : getRecurringResponse()
              }
              isSchedulePayment={isSchedulePayment}
              setIsApplePay={setIsApplePay}
              handleApplePay={handleApplePay}
              isStepper={isRetry ? true : false}
              setAmtErr={setAmtErr}
              setIsEdit={setIsEdit}
              useStripeApplePay={useStripeApplePay}
              clusterIdOverride={
                isActualPaymentLink
                  ? noAuthClusterId
                  : isAgreement
                    ? agreementClusterId
                    : clusterId
              }
              leadId={agreementLeadId}
              isPaymentLink={isActualPaymentLink}
              paymentLinkId={
                isActualPaymentLink
                  ? decode(actionId || "")?.split("_")[0]
                  : null
              }
              isAgreement={isAgreement}
              agreementId={agreementId}
              slug={isActualPaymentLink || isAgreement ? actionId : undefined}
              customConv={custom_conv}
            />
            <div className="flex items-center gap-[6px] whitespace-nowrap">
              <div className="h-[1px] w-full bg-strokePrimary"></div>
              <p className="text-sm font-normal text-txtSecondary">
                or choose payment method
              </p>
              <div className="h-[1px] w-full bg-strokePrimary"></div>
            </div>
          </div>
        ) : paymentPermission?.gp_ext_availability === SettingsValue.YES &&
          stripeKey &&
          (!useStripeGooglePay || isGooglePayAmountDisabled || isGooglePay) ? (
          <div {...testAttrs("express-wallets", "Express wallet payments")}>
            <div {...testAttrs("google-pay", "Google Pay")}>
              {isGoogleLoading ? (
                <div
                  className=" w-full bg-bgHover rounded-[5px] py-[10px] flex justify-center items-center cursor-pointer"
                  {...testAttrs("google-pay-loading", "Loading Google Pay")}
                >
                  <div
                    className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    aria-hidden="true"
                  ></div>
                </div>
              ) : useStripeGooglePay ? (
                isGooglePayAmountDisabled ? (
                  <DisabledGooglePayButton />
                ) : isGoogleIntentLoading ||
                  !googlePaySecret ||
                  !stripePromiseGooglePay ? (
                  <div
                    className=" w-full bg-bgHover rounded-[5px] py-[10px] flex justify-center items-center cursor-pointer"
                    {...testAttrs(
                      "google-pay-intent-loading",
                      "Preparing Google Pay"
                    )}
                  >
                    <div
                      className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
                      aria-hidden="true"
                    ></div>
                  </div>
                ) : (
                  <Elements
                    key={googlePaySecret}
                    stripe={stripePromiseGooglePay}
                    options={{ clientSecret: googlePaySecret }}
                  >
                    <GooglePayButton
                      amount={getPaymentTotalWithFee(amount, conFee)}
                      clientSecret={googlePaySecret}
                      orderId={googlePayOrderId}
                      onIntentFailure={markIntentFailed}
                      disabled={
                        isEdit ||
                        isFeeLoading ||
                        disableInputFields ||
                        (isProductLink && !validateEmail(email)) ||
                        (checkNegativeBalance && amountCheck)
                      }
                      handleGooglePay={async (success: any, intent: any) => {
                        if (handleGooglePay) {
                          console.info(
                            `${GOOGLE_PAY_LOG_PREFIX} forwarding confirmed wallet`,
                            {
                              hasIntent: Boolean(intent),
                              paymentMethod: "GP",
                              success,
                            }
                          );
                          await handleGooglePay(
                            success ? intent : null,
                            amount
                          );
                        }
                      }}
                      setIsGooglePay={setIsGooglePay}
                    />
                  </Elements>
                )
              ) : (
                <LegacyGooglePayButton
                  className={`${
                    isEdit ||
                    isFeeLoading ||
                    disableInputFields ||
                    (isProductLink && !validateEmail(email)) ||
                    (checkNegativeBalance && amountCheck)
                      ? "pointer-events-none cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  // A live key needs the production environment: the test
                  // one only returns test cards, which a live key rejects.
                  environment={
                    stripeKey?.startsWith("pk_live") ? "PRODUCTION" : "TEST"
                  }
                  buttonColor="white"
                  buttonType="short"
                  buttonSizeMode="fill"
                  buttonRadius={5}
                  style={{ width: "100%" }}
                  paymentRequest={{
                    apiVersion: 2,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: [
                      {
                        type: "CARD",
                        parameters: {
                          allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                          allowedCardNetworks: [
                            "AMEX",
                            "DISCOVER",
                            "JCB",
                            "MASTERCARD",
                            "VISA",
                          ],
                        },
                        tokenizationSpecification: {
                          type: "PAYMENT_GATEWAY",
                          parameters: {
                            gateway: "stripe",
                            "stripe:version": "2018-10-31",
                            "stripe:publishableKey": stripeKey,
                          },
                        },
                      },
                    ],
                    merchantInfo: {
                      merchantId: GOOGLE_MERCHANT_ID,
                      merchantName:
                        companyDetails?.name ||
                        companyDetails?.company_name ||
                        GOOGLE_MERCHANT_NAME,
                    },
                    transactionInfo: {
                      totalPriceStatus: "FINAL",
                      totalPrice: getPaymentTotalWithFee(
                        amount,
                        conFee
                      ).toFixed(2),
                      currencyCode: "USD",
                      countryCode: "US",
                    },
                  }}
                  onLoadPaymentData={(paymentData: any) => {
                    if (handleGooglePay) {
                      dispatch(setIsPaymentProcessing(true));
                      handleGooglePay(paymentData.paymentMethodData, amount);
                    }
                  }}
                  onError={(err: any) =>
                    console.log("Google Pay error =====", err)
                  }
                />
              )}
            </div>
            <div className="flex items-center gap-[6px] whitespace-nowrap">
              <div className="h-[1px] w-full bg-strokePrimary"></div>
              <p className="text-sm font-normal text-txtSecondary">
                or choose payment method
              </p>
              <div className="h-[1px] w-full bg-strokePrimary"></div>
            </div>
          </div>
        ) : null)}

      <div {...testAttrs("method-selection", "Payment method selection")}>
        {hasGooglePay && isPaymentLink && !isRetry && (
          <p className="lg:text-xl text-base font-medium text-txtPrimary flex justify-between items-center">
            Payment method
            {/* On the pages a visitor can open without logging in (payment
                link, invoice, product link, quick checkout) someone who is
                not logged in always gets this: saved methods are only loaded
                after a login, so there is no list to go by yet. The click
                opens the login, and their saved methods load once they are
                in. */}
            {!isAddNewCardMakePayment &&
            (oldCardData?.length > 0 || oldBankData?.length > 0 || !isLogin) ? (
              <span
                className="xl:text-sm text-xs text-blue cursor-pointer flex gap-2 hover:!text-btnPrimaryHover items-center"
                role="button"
                tabIndex={0}
                {...testAttrs("select-saved-method", "Select saved method")}
                onClick={() => {
                  if (isLogin) {
                    dispatch(setIsPaymentProcessing(true));
                    setIsAddNewCardMakePayment(true);
                  } else {
                    dispatch(updateOverlayState(true));
                  }
                }}
              >
                {isLogin ? (
                  <>
                    <span>
                      <WalletIcon
                        aria-hidden="true"
                        className="group"
                        _className="group-hover:!fill-btnPrimaryHover"
                      />
                    </span>
                    Select saved method
                  </>
                ) : (
                  <CustomTooltip
                    maxwidth="430px"
                    place="top"
                    id="saved-method"
                    content={`<div class="text-xs leading-[18px] font-normal">Log in to access your saved payment methods</div>`}
                  >
                    <span id="saved-method" className=" flex gap-2">
                      <span>
                        <WalletIcon
                          aria-hidden="true"
                          className="group"
                          _className="group-hover:!fill-btnPrimaryHover"
                        />
                      </span>
                      Select saved method
                    </span>
                  </CustomTooltip>
                )}
              </span>
            ) : null}
          </p>
        )}
        {/* METHOD TABS */}
        <div
          className={`${hasGooglePay && isPaymentLink && !isRetry ? "mt-[24px]" : ""} grid ${getColumns(
            paymentMethodList?.length
          )} lg:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-3 w-full`}
          {...testAttrs("method-tabs", "Payment method tabs")}
        >
          <PaymentMethodTab
            setSelectedMethod={setSelectedMethod}
            paymentMethodList={paymentMethodList}
            selectedMethod={selectedMethod}
          />
        </div>
      </div>

      {/* DISPLAYS SELECTED OPRIONS */}
      {selectedMethod === "Card" ? (
        // `empty:hidden` because this wrapper renders with no children in the
        // retry flow, where the card form is mounted by the host instead. As a
        // flex child it still claimed a full gap slot, showing up as a band of
        // dead space between the method tabs and the card fields.
        <div
          className="empty:hidden"
          {...testAttrs("card-panel", "Card payment details")}
        >
          {isStripeKeyMissing(paymentPermission, "CC") ? (
            <div
              role="alert"
              className="text-xs text-red pt-3"
              {...testAttrs(
                "stripe-card-unavailable",
                "Card payments unavailable"
              )}
            >
              {STRIPE_CARD_UNAVAILABLE_MESSAGE}
            </div>
          ) : paymentPermission?.cc_gateway_name?.name ===
              PaymentGateway.STRIPE && stripePromiseCard ? (
            !isRetry && (
              <div {...testAttrs("stripe-card-form", "Stripe card form")}>
                <Elements stripe={stripePromiseCard}>
                  <StripeCardPaymentCheckoutForm
                    values={values}
                    handleChange={handleChangeCard}
                    isEdit={false}
                    iconType={iconType}
                    selectedState={selectedState}
                    setSelectedState={setSelectedState}
                    cardErrMsg={cardErrMsg}
                    amount={amount}
                    email={email}
                    setEmail={setEmail}
                    isPaymentLink={isPaymentLink}
                    setShowPreview={setShowPreview}
                    isRecurring={getRecurringResponse()}
                    paymentPlan={dataArr}
                    isAgreement={isAgreement}
                    handleBack={handleBack}
                    isUpdatePlan={isUpdatePlan}
                    isSchedulePayment={isSchedulePayment}
                    isUpdateSubscription={isUpdateSubscription}
                    isUpdateSubscriptionPaymentMethod={
                      isUpdateSubscriptionPaymentMethod
                    }
                    isDowngradePlan={isDowngradePlan}
                    ref={cardRef}
                    showConvFee={showConvFee}
                    checkNegativeBalance={checkNegativeBalance}
                    isProductLink={isProductLink}
                    accounts_id={accounts_id}
                    setIconType={setIconType}
                    collectAddress={collectAddress}
                    collectPhone={collectPhone}
                    verifyStripeCardPayment={verifyStripeCardPayment}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    description={description}
                    formCheck={formCheck}
                    setFormCheck={setFormCheck}
                    formEmptyCheck={formEmptyCheck}
                    setFormEmptyCheck={setFormEmptyCheck}
                    handleEmailBlur={handleEmailBlur}
                    isInvoice={isInvoice}
                  />
                </Elements>
              </div>
            )
          ) : paymentPermission?.cc_gateway_name?.name ===
            PaymentGateway.STAX ? (
            <div {...testAttrs("stax-card-form", "Stax card form")}>
              <StaxCardForm
                values={values}
                handleChange={handleChangeCard}
                isEdit={false}
                iconType={iconType}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                validateCard={validateCard}
                isErr={isErr}
                cardErrMsg={cardErrMsg}
                disableInputFields={disableInputFields}
                amount={amount}
                isRecurring={getRecurringResponse()}
                setIconType={setIconType}
                setEmail={setEmail}
                setPhoneNumber={setPhoneNumber}
                email={email}
                isPaymentLink={isPaymentLink}
                setShowPreview={setShowPreview}
                paymentPlan={dataArr}
                isAgreement={isAgreement}
                handleBack={handleBack}
                isUpdatePlan={isUpdatePlan}
                isSchedulePayment={isSchedulePayment}
                isUpdateSubscription={isUpdateSubscription}
                isUpdateSubscriptionPaymentMethod={
                  isUpdateSubscriptionPaymentMethod
                }
                isDowngradePlan={isDowngradePlan}
                showConvFee={showConvFee}
                checkNegativeBalance={checkNegativeBalance}
                isProductLink={isProductLink}
                accounts_id={accounts_id}
                collectAddress={collectAddress}
                collectPhone={collectPhone}
                verifyStaxCardBank={verifyStaxCardBank}
                phoneNumber={phoneNumber}
                description={description}
                handleEmailBlur={handleEmailBlur}
              />
            </div>
          ) : (
            <div className={` ${isAgreement ? "lg:mb-10" : ""} w-full h-full`}>
              <div className="flex flex-col gap-5 items-start">
                <CardFormComponent
                  values={values}
                  handleChange={handleChangeCard}
                  isEdit={false}
                  cardNumber={cardNumber}
                  iconType={iconType}
                  isDateErr={isDateErr}
                  selectedState={selectedState}
                  setSelectedState={setSelectedState}
                  validateCard={validateCard}
                  isErr={isErr}
                  cardErrMsg={cardErrMsg}
                  disableInputFields={disableInputFields}
                />
              </div>
              <div>
                {showConvFee &&
                  portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
                    <ConvenienceFeeDisplay
                      conFee={conFee}
                      isFeeLoading={isFeeLoading}
                      isPaymentLink={isPaymentLink}
                      setShowPreview={setShowPreview}
                    />
                  )}
                {isErr === 4 && (
                  <div className="text-xs text-red pt-3">{cardErrMsg}</div>
                )}
              </div>

              <InputField
                showLabel={true}
                {...testAttrs("card-email", "Card payment email")}
                label="EMAIL"
                name="email"
                type="text"
                placeholder="Email address"
                onChange={(e) => {
                  setEmail(
                    accountData?.email ? accountData?.email : e.target.value
                  );
                }}
                onBlur={handleEmailBlur}
                value={accountData?.email ? accountData?.email : email}
                disabled={accountData?.email || disableInputFields}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
                _className="!text-xs !font-bold !leading-normal"
              />

              {collectPhone && (
                <div className="pt-5">
                  <div
                    className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                  >
                    Phone
                  </div>
                  <MobileInputNew
                    inputValue={phoneNumber?.phone || ""}
                    isProductLinkInput={true}
                    aria-label="Card payment phone number"
                    data-testid={testAttrs("card-phone")["data-testid"]}
                    userCountry={
                      getCountryShortName(phoneNumber?.country_code) || "US"
                    }
                    setCountry={handlePhoneCountryChange}
                    setInputValue={handleOnPhoneChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ) : selectedMethod === "Bank" ? (
        paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE &&
        stripePromiseBank ? (
          !isRetry &&
          (bankSecret ? (
            <Elements stripe={stripePromiseBank} options={bankFormOptions}>
              <div {...testAttrs("stripe-bank-form", "Stripe bank form")}>
                <StripeBankForm
                  amount={amount}
                  isEdit={isEdit}
                  email={email}
                  setEmail={setEmail}
                  isSecretLoading={isLoading}
                  isRecurring={getRecurringResponse()}
                  isUpdateSubscriptionPaymentMethod={
                    isUpdateSubscriptionPaymentMethod
                  }
                  isDowngradePlan={isDowngradePlan}
                  paymentPlan={dataArr}
                  stripeBankURL={stripeBankURL}
                  isPaymentLink={isPaymentLink}
                  isAgreement={isAgreement}
                  handleBack={handleBack}
                  isUpdatePlan={isUpdatePlan}
                  setShowPreview={setShowPreview}
                  isSchedulePayment={isSchedulePayment}
                  ref={bankRef}
                  isProductLink={isProductLink}
                  isUpdateSubscription={isUpdateSubscription}
                  checkNegativeBalance={checkNegativeBalance}
                  collectPhone={collectPhone}
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  showConvFee={showConvFee}
                  handleEmailBlur={handleEmailBlur}
                  orderId={bankOrderId}
                  onIntentFailure={markIntentFailed}
                />
              </div>
            </Elements>
          ) : isAgreement ? (
            <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
              <Button
                text={`Back`}
                variant="white"
                {...testAttrs("bank-back", "Back")}
                onClick={handleBack}
                className="lg:w-40 w-full max-w-40"
              />
              <Button
                text={`Confirm`}
                {...testAttrs("bank-confirm", "Confirm bank payment")}
                isDisabled={amountCheck}
                isLoading={isLoading}
                className="lg:w-40 w-full max-w-40"
                variant="variable"
              />
            </div>
          ) : isSchedulePayment ? (
            <div className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5">
              <div
                className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
              >
                {isUpdatePlan && (
                  <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
                )}

                <div className="w-full flex justify-between gap-5">
                  <Button
                    className="lg:!w-40 !w-full"
                    isDisabled={isLoading}
                    text="Back"
                    type="button"
                    variant="secondary"
                    {...testAttrs("bank-back", "Back")}
                    onClick={handleBack}
                  />
                  <Button
                    className="lg:!w-40 !w-full"
                    isLoading={isLoading}
                    isDisabled={amountCheck}
                    text={isUpdatePlan ? "Update" : "Confirm"}
                    type="button"
                    variant="variable"
                    {...testAttrs(
                      "bank-confirm",
                      isUpdatePlan
                        ? "Update bank payment"
                        : "Confirm bank payment"
                    )}
                  />
                </div>
              </div>
            </div>
          ) : (
            <Button
              text={
                isUpdatePlan || isUpdateSubscription
                  ? "Update"
                  : `Pay ${formatCurrency(amount) || 0.0}`
              }
              className="!w-full"
              {...testAttrs(
                "bank-submit",
                isUpdatePlan || isUpdateSubscription
                  ? "Update bank payment"
                  : "Pay with bank"
              )}
              isDisabled={amountCheck || !validateEmail(email)}
              isLoading={isLoading}
              variant="variable"
            />
          ))
        ) : paymentPermission?.ach_gateway_name?.name ===
          PaymentGateway.STAX ? (
          <div {...testAttrs("stax-bank-form", "Stax bank form")}>
            <StaxBankForm
              values={values}
              handleChange={handleChangeBank}
              numberNotmatchedError={numberNotmatchedError}
              setnumberNotmatchedError={setnumberNotmatchedError}
              errMsg={errMsg}
              bankName={bankName}
              isRingLoading={isRingLoading}
              email={email}
              setEmail={setEmail}
              setShowPreview={setShowPreview}
              isPaymentLink={isPaymentLink}
              disableInputFields={disableInputFields}
              showConvFee={showConvFee}
              collectPhone={collectPhone}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              amount={amount}
              isEdit={isEdit}
              isSecretLoading={isLoading}
              isRecurring={getRecurringResponse()}
              isUpdateSubscriptionPaymentMethod={
                isUpdateSubscriptionPaymentMethod
              }
              isDowngradePlan={isDowngradePlan}
              paymentPlan={dataArr}
              isAgreement={isAgreement}
              handleBack={handleBack}
              isUpdatePlan={isUpdatePlan}
              isSchedulePayment={isSchedulePayment}
              isProductLink={isProductLink}
              isUpdateSubscription={isUpdateSubscription}
              checkNegativeBalance={checkNegativeBalance}
              verifyStaxCardBank={verifyStaxCardBank}
              handleEmailBlur={handleEmailBlur}
            />
          </div>
        ) : checkBankManualFormOrPlaid({
            manual_ach: portalSettings?.manual_ach,
            validate_bank_account: portalSettings?.validate_bank_account,
            isManualForm,
          }) ? (
          <div>
            {portalSettings?.manual_ach === SettingsValue.YES && (
              <div className="mb-4 text-xs leading-[15.62px] w-fit">
                <span
                  className="text-blue cursor-pointer font-medium hover:!text-btnPrimaryHover"
                  role="button"
                  tabIndex={0}
                  {...testAttrs(
                    "enter-bank-manually",
                    "Enter bank details manually"
                  )}
                  onClick={() => setIsManualForm(true)}
                >
                  Enter bank details manually instead
                </span>{" "}
                <span>(takes 1 - 2 business days)</span>
              </div>
            )}
            {showConvFee &&
              portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
                <ConvenienceFeeDisplay
                  conFee={conFee}
                  isFeeLoading={isFeeLoading}
                  isPaymentLink={isPaymentLink}
                  setShowPreview={setShowPreview}
                  customClassName=""
                />
              )}
            <div className="w-full pt-5 pb-3">
              <InputField
                showLabel={true}
                {...testAttrs("bank-email", "Bank payment email")}
                label="EMAIL"
                name="email"
                type="text"
                placeholder="Email address"
                onChange={(e) => {
                  setIsEmailInputBlur(false);
                  setEmail(
                    userData?.data?.accountData?.email
                      ? userData?.data?.accountData?.email
                      : e.target.value
                  );
                }}
                onBlur={() => {
                  setIsEmailInputBlur(true);
                  handleEmailBlur();
                }}
                value={
                  userData?.data?.accountData?.email
                    ? userData?.data?.accountData?.email
                    : email
                }
                disabled={!!userData?.data?.accountData?.email}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
                _className="!text-xs !font-bold !leading-normal"
              />

              {collectPhone && (
                <div className="pt-5">
                  <div
                    className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                  >
                    Phone
                  </div>
                  <MobileInputNew
                    inputValue={phoneNumber?.phone || ""}
                    isProductLinkInput={true}
                    aria-label="Bank payment phone number"
                    data-testid={testAttrs("bank-phone")["data-testid"]}
                    userCountry={
                      getCountryShortName(phoneNumber?.country_code) || "US"
                    }
                    setCountry={handlePhoneCountryChange}
                    setInputValue={handleOnPhoneChange}
                  />
                </div>
              )}
            </div>
            {!isRetry && (
              <div {...testAttrs("plaid-bank-form", "Plaid bank form")}>
                <PlaidBankForm
                  amount={amount}
                  linkToken={plaidLinkToken}
                  isLoading={isLinkTokenLoading}
                  handlePlaidPayment={handlePlaidBankPayment}
                  isAgreement={isAgreement}
                  handleBack={handleBack}
                  isUpdatePlan={isUpdatePlan}
                  isUpdateSubscriptionPaymentMethod={
                    isUpdateSubscriptionPaymentMethod
                  }
                  isDowngradePlan={isDowngradePlan}
                  isSchedulePayment={isSchedulePayment}
                  bankRef={bankRef}
                  email={email}
                  isEdit={isEdit}
                  isRetry={isRetry}
                  isEmailInputBlur={isEmailInputBlur}
                  isProductLink={isProductLink}
                  isUpdateSubscription={isUpdateSubscription}
                  checkNegativeBalance={checkNegativeBalance}
                  collectPhone={collectPhone}
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                />
              </div>
            )}
          </div>
        ) : (
          <div
            className={`  ${
              isAgreement ? "lg:mb-10" : ""
            } flex flex-col w-full lg:gap-[30px] gap-5`}
            {...testAttrs("manual-bank-form", "Manual bank form")}
          >
            <BankMethodComponent
              values={values}
              handleChange={handleChangeBank}
              numberNotmatchedError={numberNotmatchedError}
              setnumberNotmatchedError={setnumberNotmatchedError}
              errMsg={errMsg}
              bankName={bankName}
              isRingLoading={isRingLoading}
              setEmails={setEmail}
              setShowPreview={setShowPreview}
              isPaymentLink={isPaymentLink}
              setIsManualForm={setIsManualForm}
              disableInputFields={disableInputFields}
              showConvFee={showConvFee}
              collectPhone={collectPhone}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              handleEmailBlur={handleEmailBlur}
            />
          </div>
        )
      ) : selectedMethod === "Cash App Pay" ? (
        !clientSecret && !isRetry ? (
          <div {...testAttrs("cash-app-panel", "Cash App Pay details")}>
            <div
              className="flex gap-4 py-3 px-6 border-[1px] border-strokePrimary rounded"
              {...testAttrs("cash-app-instructions")}
            >
              <CashAppMobileIcon aria-hidden="true" />
              <p className="lg:text-base text-sm text-txtSecondary">
                After submitting your order, scan the QR code using Cash App
                Pay.
              </p>
            </div>
            {showConvFee &&
              portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
                <ConvenienceFeeDisplay
                  conFee={conFee}
                  isFeeLoading={isFeeLoading}
                  isPaymentLink={isPaymentLink}
                  setShowPreview={setShowPreview}
                  customClassName="mt-2 mb-5"
                />
              )}

            {isProductLink && (
              <div className="py-4">
                <InputField
                  showLabel={true}
                  {...testAttrs("cash-app-email", "Cash App payment email")}
                  label="EMAIL"
                  name="email"
                  type="text"
                  placeholder="Email address"
                  onChange={(e) => {
                    setEmail(
                      userData?.data?.accountData?.email
                        ? userData?.data?.accountData?.email
                        : e.target.value
                    );
                  }}
                  onBlur={handleEmailBlur}
                  value={
                    userData?.data?.accountData?.email
                      ? userData?.data?.accountData?.email
                      : email
                  }
                  disabled={userData?.data?.accountData?.email}
                  className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
                  _className="!text-xs !font-bold !leading-normal"
                />

                {collectPhone && (
                  <div className="pt-5">
                    <div
                      className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                    >
                      Phone
                    </div>
                    <MobileInputNew
                      inputValue={phoneNumber?.phone || ""}
                      isProductLinkInput={true}
                      aria-label="Cash App payment phone number"
                      data-testid={testAttrs("cash-app-phone")["data-testid"]}
                      userCountry={
                        getCountryShortName(phoneNumber?.country_code) || "US"
                      }
                      setCountry={handlePhoneCountryChange}
                      setInputValue={handleOnPhoneChange}
                    />
                  </div>
                )}
              </div>
            )}
            {isAgreement ? (
              <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
                <Button
                  className="lg:!w-40 !w-full max-w-40"
                  isDisabled={isLoading}
                  text="Back"
                  type="button"
                  variant="secondary"
                  {...testAttrs("cash-app-back", "Back")}
                  onClick={handleBack}
                />
                <Button
                  className="lg:!w-48 !w-full max-w-40"
                  isLoading={isLoading}
                  isDisabled={true}
                  text="Confirm"
                  type="button"
                  variant="variable"
                  {...testAttrs("cash-app-confirm", "Confirm Cash App payment")}
                />
              </div>
            ) : isSchedulePayment ? (
              <div className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5">
                <div
                  className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
                >
                  {isUpdatePlan && (
                    <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
                  )}

                  <div className="w-full flex justify-between gap-5">
                    <Button
                      className="lg:!w-40 !w-full"
                      isDisabled={isLoading}
                      text="Back"
                      type="button"
                      variant="secondary"
                      {...testAttrs("cash-app-back", "Back")}
                      onClick={handleBack}
                    />
                    <Button
                      className="lg:!w-48 !w-full"
                      isLoading={isLoading}
                      isDisabled={false}
                      text={isUpdatePlan ? "Update" : "Reveal QR code"}
                      type="button"
                      variant="variable"
                      {...testAttrs(
                        "cash-app-submit",
                        isUpdatePlan
                          ? "Update Cash App payment"
                          : "Reveal Cash App QR code"
                      )}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Button
                text={
                  isUpdatePlan ||
                  isUpdateSubscription ||
                  isUpdateSubscriptionPaymentMethod
                    ? "Update"
                    : "Reveal QR code"
                }
                className="!w-full"
                {...testAttrs("cash-app-submit", "Reveal Cash App QR code")}
                isDisabled={
                  amountCheck ||
                  isLoading ||
                  !validateEmail(email) ||
                  (collectPhone && !checkPhone(phoneNumber))
                }
                isLoading={isLoading}
                variant="variable"
              />
            )}
          </div>
        ) : clientSecret && !isRetry ? (
          <>
            {stripePromiseCashApp && (
              <Elements
                stripe={stripePromiseCashApp}
                options={{ clientSecret }}
              >
                <div {...testAttrs("cash-app-form", "Cash App payment form")}>
                  <CashAppForm
                    isRecurring={getRecurringResponse()}
                    isLoading={isLoading}
                    paymentPlan={dataArr}
                    isDisabled={amountCheck}
                    cashAppURL={cashAppURL}
                    isSchedulePayment={isSchedulePayment}
                    handleBack={handleBack}
                    ref={cashAppRef}
                    isProductLink={isProductLink}
                    email={email}
                    setEmail={setEmail}
                    isPaymentLink={isPaymentLink}
                    setShowPreview={setShowPreview}
                    isUpdateSubscription={isUpdateSubscription}
                    isUpdatePlan={isUpdatePlan}
                    amount={amount}
                    checkNegativeBalance={checkNegativeBalance}
                    isAgreement={isAgreement}
                    accounts_id={accounts_id}
                    collectPhone={collectPhone}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    isUpdateSubscriptionPaymentMethod={
                      isUpdateSubscriptionPaymentMethod
                    }
                    isDowngradePlan={isDowngradePlan}
                    showConvFee={showConvFee}
                    handleEmailBlur={handleEmailBlur}
                    orderId={cashAppOrderId}
                    onIntentFailure={markIntentFailed}
                  />
                </div>
              </Elements>
            )}
          </>
        ) : (
          <>
            <div {...testAttrs("cash-app-panel", "Cash App Pay details")}>
              <div
                className="flex gap-4 py-3 px-6 border-[1px] border-strokePrimary rounded"
                {...testAttrs("cash-app-instructions")}
              >
                <div>
                  <CashAppMobileIcon aria-hidden="true" />
                </div>
                <p className="lg:text-base text-sm text-txtSecondary">
                  After submitting your order, scan the QR code using Cash App
                  Pay.
                </p>
              </div>
              {showConvFee &&
                portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
                  <ConvenienceFeeDisplay
                    conFee={conFee}
                    isFeeLoading={isFeeLoading}
                    isPaymentLink={isPaymentLink}
                    setShowPreview={setShowPreview}
                    customClassName="mt-2 mb-5"
                  />
                )}
            </div>
          </>
        )
      ) : selectedMethod === "PayPal" &&
        paymentPermission?.pp_ext_availability === "Y" ? (
        <div {...testAttrs("paypal-panel", "PayPal payment details")}>
          <PayPalForm
            amount={amount}
            isAgreement={isAgreement}
            isSchedulePayment={isSchedulePayment}
            isRetry={isRetry}
            isUpdatePlan={isUpdatePlan}
            isEdit={isEdit}
            isProductLink={isProductLink}
            collectPhone={collectPhone}
            phoneNumber={phoneNumber}
            handlePhoneCountryChange={handlePhoneCountryChange}
            handleOnPhoneChange={handleOnPhoneChange}
            email={email}
            setEmail={setEmail}
            userData={userData}
            createPayPalIntent={createPayPalIntent}
            handleBack={handleBack}
            amtErr={amtErr}
            isStaxPayPalLoading={isStaxPayPalLoading}
            verifyStaxPayPal={verifyStaxPayPal}
            handleEmailBlur={handleEmailBlur}
          />
        </div>
      ) : selectedMethod === "Wire" ? (
        <div {...testAttrs("wire-panel", "Wire transfer details")}>
          <div className="flex w-full gap-[6px] justify-start items-start">
            <InfoIcon className="w-5 h-5 pt-1" aria-hidden="true" />
            <p className="lg:text-base text-sm text-txtSecondary">
              Upon completing the payment process, you will be provided with the
              wire transfer instructions.
            </p>
          </div>
          {isProductLink && (
            <div>
              <InputField
                showLabel={true}
                {...testAttrs("wire-email", "Wire transfer email")}
                label="EMAIL"
                name="email"
                type="text"
                placeholder="Email address"
                onChange={(e) => {
                  setEmail(
                    userData?.data?.accountData?.email
                      ? userData?.data?.accountData?.email
                      : e.target.value
                  );
                }}
                onBlur={handleEmailBlur}
                value={
                  userData?.data?.accountData?.email
                    ? userData?.data?.accountData?.email
                    : email
                }
                disabled={userData?.data?.accountData?.email}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
                _className="!text-xs !font-bold !leading-normal"
              />

              {collectPhone && (
                <div className="pt-5">
                  <div
                    className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                  >
                    Phone
                  </div>
                  <MobileInputNew
                    inputValue={phoneNumber?.phone || ""}
                    isProductLinkInput={true}
                    aria-label="Wire transfer phone number"
                    data-testid={testAttrs("wire-phone")["data-testid"]}
                    userCountry={
                      getCountryShortName(phoneNumber?.country_code) || "US"
                    }
                    setCountry={handlePhoneCountryChange}
                    setInputValue={handleOnPhoneChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ) : selectedMethod === "Amazon Pay" ? (
        <div {...testAttrs("amazon-pay-panel", "Amazon Pay details")}>
          <div
            className="flex gap-4 py-3 px-6 border-[1px] border-strokePrimary rounded"
            {...testAttrs("amazon-pay-instructions")}
          >
            <div>
              <CashAppMobileIcon aria-hidden="true" />
            </div>
            <p className="lg:text-base text-sm text-txtSecondary">
              After submission, you will be redirected to securely complete next
              steps.
            </p>
          </div>
          <p className="mt-4 text-txtParagraph text-sm lg:text-base font-normal">
            By confirming your payment with Amazon Pay, you allow{" "}
            {companyDetails?.name || companyDetails?.company_name} to charge
            your Amazon Pay account for future payments in accordance with their
            terms.
          </p>
          {showConvFee &&
            portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
              <div className={isAgreement ? "mb-10" : ""}>
                <ConvenienceFeeDisplay
                  conFee={conFee}
                  isFeeLoading={isFeeLoading}
                  isPaymentLink={isPaymentLink}
                  setShowPreview={setShowPreview}
                  customClassName="mt-5 mb-5"
                />
                {isErr === 4 && (
                  <div className="text-xs text-red pt-3">{cardErrMsg}</div>
                )}
              </div>
            )}

          {amazonSecret && !isRetry ? (
            <>
              {stripePromiseAmazon && (
                <Elements
                  stripe={stripePromiseAmazon}
                  options={{ clientSecret: amazonSecret }}
                >
                  <div
                    {...testAttrs("amazon-pay-form", "Amazon Pay payment form")}
                  >
                    <AmazonPayForm
                      isSchedulePayment={isSchedulePayment}
                      handleBack={handleBack}
                      isUpdatePlan={isUpdatePlan}
                      amount={amount}
                      isRetry={isRetry}
                      isEdit={isEdit}
                      isSecretLoading={isLoading}
                      isRecurring={getRecurringResponse()}
                      isUpdateSubscriptionPaymentMethod={
                        isUpdateSubscriptionPaymentMethod
                      }
                      isDowngradePlan={isDowngradePlan}
                      paymentPlan={dataArr}
                      stripeAmazonPayURL={stripeAmazonPayURL}
                      ref={amazonRef}
                      isAgreement={isAgreement}
                      isProductLink={isProductLink}
                      email={email}
                      setEmail={setEmail}
                      isUpdateSubscription={isUpdateSubscription}
                      checkNegativeBalance={checkNegativeBalance}
                      accounts_id={accounts_id}
                      collectPhone={collectPhone}
                      phoneNumber={phoneNumber}
                      setPhoneNumber={setPhoneNumber}
                      handleEmailBlur={handleEmailBlur}
                      orderId={amazonOrderId}
                      onIntentFailure={markIntentFailed}
                    />
                  </div>
                </Elements>
              )}
            </>
          ) : (
            <>
              {isProductLink && (
                <div className="py-4">
                  <InputField
                    showLabel={true}
                    {...testAttrs("amazon-pay-email", "Amazon Pay email")}
                    label="EMAIL"
                    name="email"
                    type="text"
                    placeholder="Email address"
                    onChange={(e) => {
                      setEmail(
                        userData?.data?.accountData?.email
                          ? userData?.data?.accountData?.email
                          : e.target.value
                      );
                    }}
                    onBlur={handleEmailBlur}
                    value={
                      userData?.data?.accountData?.email
                        ? userData?.data?.accountData?.email
                        : email
                    }
                    disabled={userData?.data?.accountData?.email}
                    className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
                    _className="!text-xs !font-bold !leading-normal"
                  />

                  {collectPhone && (
                    <div className="pt-5">
                      <div
                        className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                      >
                        Phone
                      </div>
                      <MobileInputNew
                        inputValue={phoneNumber?.phone || ""}
                        isProductLinkInput={true}
                        aria-label="Amazon Pay phone number"
                        data-testid={
                          testAttrs("amazon-pay-phone")["data-testid"]
                        }
                        userCountry={
                          getCountryShortName(phoneNumber?.country_code) || "US"
                        }
                        setCountry={handlePhoneCountryChange}
                        setInputValue={handleOnPhoneChange}
                      />
                    </div>
                  )}
                </div>
              )}
              {isAgreement && !isRetry ? (
                <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
                  <Button
                    text={`Back`}
                    variant="white"
                    {...testAttrs("amazon-pay-back", "Back")}
                    onClick={handleBack}
                    className="lg:w-40 w-full max-w-40"
                  />
                  <Button
                    text={`Confirm`}
                    {...testAttrs("amazon-pay-confirm", "Confirm Amazon Pay")}
                    isDisabled={amountCheck}
                    isLoading={isLoading}
                    className="lg:w-40 w-full max-w-40"
                    variant="variable"
                  />
                </div>
              ) : isSchedulePayment && !isRetry ? (
                <div className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5">
                  <div
                    className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
                  >
                    {isUpdatePlan && (
                      <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
                    )}

                    <div className="w-full flex justify-between gap-5">
                      <Button
                        className="lg:!w-40 !w-full"
                        isDisabled={isLoading}
                        text="Back"
                        type="button"
                        variant="secondary"
                        {...testAttrs("amazon-pay-back", "Back")}
                        onClick={handleBack}
                      />
                      <Button
                        className="lg:!w-40 !w-full"
                        isLoading={isLoading}
                        isDisabled={amountCheck}
                        text={isUpdatePlan ? "Update" : "Confirm"}
                        type="button"
                        variant="variable"
                        {...testAttrs(
                          "amazon-pay-confirm",
                          isUpdatePlan
                            ? "Update Amazon Pay"
                            : "Confirm Amazon Pay"
                        )}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                !isRetry && (
                  <Button
                    text={
                      isUpdatePlan || isUpdateSubscription
                        ? "Update"
                        : `Pay ${formatCurrency(amount) || 0.0}`
                    }
                    className="!w-full"
                    {...testAttrs("amazon-pay-submit", "Pay with Amazon Pay")}
                    isDisabled={
                      amountCheck ||
                      !validateEmail(email) ||
                      (collectPhone && !checkPhone(phoneNumber))
                    }
                    isLoading={isLoading}
                    variant="variable"
                  />
                )
              )}
            </>
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default PaymentMethodsComponent;
